import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// Actions
import { fetchTeamInfo } from '../../actions/TeamInfo.actions';

// Hooks
import { useWorkspace } from './use-workspace';
import { useToggle } from 'Shared/utils/hooks';
import { useFetchBulkBAData } from './use-fetch-bulk-ba-data';
import { useOrganization } from 'Shared/utils/hooks/use-organization';

// Types
import { IndividualContributionInsight } from 'Shared/types/TeamInfo.types';

//Util
import { getBADemoData, getTeamInfoDemoData } from 'Shared/utils/piDemoData.utils';

// Selectors
import {
  getTeamInfoByWorkspaceSlug,
  getTeamInfoLoading,
  getTeamInfoError,
  getTeamType,
  getShortTeamDescription,
  getTeamConversationTips,
  getTeamReminders,
  getIndividualContributionInsight,
} from '../../selectors/TeamInfo.selectors';
import { getAllBaInfo } from 'Shared/selectors/BaInfo.selectors';

export const useTeamInfo = () => {
  const dispatch = useDispatch();
  const { users, workspace } = useWorkspace();
  const [isFetching, toggleIsFetching] = useToggle(false);
  const { isPiDemoAccount } = useOrganization();

  // Get BA data for all users
  let allBaInfo = useSelector(getAllBaInfo);
  const { isLoading: isLoadingBA, usersWithBA, isFetchingBAData } = useFetchBulkBAData(users);

  // Use selectors to get team info data
  const teamInfoCache = useSelector(state => getTeamInfoByWorkspaceSlug(state, { workspaceSlug: workspace.id }));
  const loadingTeamInfo = useSelector(getTeamInfoLoading);
  const errorTeamInfo = useSelector(getTeamInfoError);

  // Get specific team info
  let teamType = useSelector(state => getTeamType(state, { workspaceSlug: workspace.id }));
  let shortTeamDescription = useSelector(state => getShortTeamDescription(state, { workspaceSlug: workspace.id }));
  let teamConversationTips = useSelector(state => getTeamConversationTips(state, { workspaceSlug: workspace.id }));
  let teamReminders = useSelector(state => getTeamReminders(state, { workspaceSlug: workspace.id }));
  let individualContributionInsight: IndividualContributionInsight[] = useSelector(state =>
    getIndividualContributionInsight(state, { workspaceSlug: workspace.id })
  );

  if (isPiDemoAccount) {
    const demoTeamData = getTeamInfoDemoData();
    teamType = demoTeamData.teamType;
    shortTeamDescription = demoTeamData.shortTeamDescription;
    teamConversationTips = demoTeamData.teamConversationTips;
    teamReminders = demoTeamData.teamReminders;
    individualContributionInsight = demoTeamData.individualContributionInsight;
    allBaInfo = users.map(user => getBADemoData(user.firstName));
  }

  const members = useMemo(() => {
    if (isPiDemoAccount) {
      return allBaInfo.map(ba => ({
        surveyUserGuid: ba.assessmentUserId,
        selfAssessmentScore: ba.selfAssessmentScore,
      }));
    }

    if (!users || users.length === 0) return [];

    return users
      .filter(user => user.piUserGuid)
      .map(user => {
        const hasBA = usersWithBA.find(userWithBA => userWithBA.piUserGuid === user.piUserGuid);

        if (!hasBA) {
          return null;
        }

        const baInfo = allBaInfo.get(user.piUserGuid)?.toJS();

        const selfPatternData = {
          a: baInfo?.selfAssessmentScore?.aSigma,
          b: baInfo?.selfAssessmentScore?.bSigma,
          c: baInfo?.selfAssessmentScore?.cSigma,
          d: baInfo?.selfAssessmentScore?.dSigma,
        };

        return {
          surveyUserGuid: user.piUserGuid,
          selfPatternData,
        };
      })
      .filter(Boolean);
  }, [users, usersWithBA, allBaInfo]);

  useEffect(() => {
    if (isLoadingBA || !workspace.id || members.length === 0 || teamInfoCache || loadingTeamInfo || isFetchingBAData) {
      return;
    }

    toggleIsFetching();

    const fetchData = async () => {
      try {
        await dispatch(fetchTeamInfo({ members }, workspace.id));
      } catch (error) {
        console.error('Error fetching team info:', error);
      } finally {
        toggleIsFetching();
      }
    };

    fetchData();
  }, [usersWithBA]);

  return {
    teamType,
    shortTeamDescription,
    teamConversationTips,
    teamReminders,
    individualContributionInsight,
    loading: loadingTeamInfo || isLoadingBA || (!teamInfoCache && isFetching),
    error: errorTeamInfo,
    isFetching,
  };
};
